import { useEffect, useState } from 'react';

import type { CategoryPage } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export type CategoryViewProps = {
  lang: string;
  name: string;
  onNavigate: (slug: string) => void;
};

export function CategoryView({ lang, name, onNavigate }: CategoryViewProps) {
  const t = useT();
  const [data, setData] = useState<CategoryPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    wikiApiClient
      .getCategory(lang, name)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('category_error_load'));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lang, name, t]);

  function loadMore() {
    if (!data?.next_cursor || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    wikiApiClient
      .getCategory(lang, name, { cursor: data.next_cursor })
      .then((result) => {
        setData((prev) => (prev ? { ...result, pages: [...prev.pages, ...result.pages] } : result));
        setIsLoadingMore(false);
      })
      .catch(() => {
        setIsLoadingMore(false);
      });
  }

  const pages = data?.pages ?? [];

  return (
    <article className="note page-index" data-testid="category-view">
      <header className="note-header">
        <div className="note-kicker">
          <span>{t('category_kicker')}</span>
        </div>
        <h1>{name}</h1>
      </header>

      {isLoading ? <div className="empty-note">{t('category_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}
      {!isLoading && !error && pages.length === 0 ? (
        <div className="empty-note">{t('category_empty')}</div>
      ) : null}
      {!isLoading && !error && pages.length > 0 ? (
        <ul className="page-index-list">
          {pages.map((page) => (
            <li key={page.id || page.slug}>
              <a
                href={`/pages/${encodeURIComponent(page.slug)}`}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(page.slug);
                }}
              >
                <strong>{page.title}</strong>
                <span>{page.slug}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {data?.next_cursor ? (
        <button disabled={isLoadingMore} type="button" onClick={loadMore}>
          {isLoadingMore ? t('category_load_more_loading') : t('category_load_more')}
        </button>
      ) : null}
    </article>
  );
}
